import { FunctionComponent, useState } from 'react';
import './styles/NoticeThemeManagerModal.scss';

import { Box, Stack, Typography } from '@mui/material';

import {
  Button,
  CenterProgressSpinner,
  HStack,
  IModalPropsBase,
  ManagedItemList,
  Modal
} from '@dw/components/core';
import { useConfirm } from '@dw/hooks';
import { useDeleteNoticeThemeMutation, useGetNoticeThemes } from '@dw/hooks/api/NoticeThemesApi';
import { useGetNotices } from '@dw/hooks/api/NoticesApi';
import { ISchoolNoticeTheme, NOTICE_THEME_MAX_PER_SCHOOL, noticeThemeBorderSx, noticeThemeName } from '@dw/models/notice';
import { componentClassName, getSpacingCss, plurify } from '@dw/utils';

import { NoticeThemeEditorModal } from './NoticeThemeEditorModal';

/**
 * The school's palette, as a list of the themes themselves.
 *
 * Each row wears its own theme rather than sitting beside a swatch, because the row IS the thing
 * being managed — a theme whose text is illegible on its own background says so here, in the list,
 * without anybody having to open it.
 *
 * Every theme is editable and deletable, the seven built-ins included. A studio who wants "Butter"
 * to be their own gold edits Butter, and the notices already pinned on it follow.
 */
export const NoticeThemeManagerModal: FunctionComponent<IModalPropsBase> = (props: IModalPropsBase) => {

  const { open, onClose } = props;

  const { confirm } = useConfirm();

  const { data: themes, isLoading } = useGetNoticeThemes({ enabled: open });
  // Already in the cache from the board behind this dialog, so counting what a theme is used by
  // costs nothing. It is only ever used to warn before a delete.
  const { data: notices } = useGetNotices({ enabled: open });

  const { mutate: deleteTheme } = useDeleteNoticeThemeMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [themeBeingEdited, setThemeBeingEdited] = useState<ISchoolNoticeTheme | undefined>();

  const cn = componentClassName('notice-theme-manager-modal');

  const themeList = themes ?? [];

  const noticesUsing = (theme: ISchoolNoticeTheme) =>
    (notices ?? []).filter(x => x.themeId === theme.noticeThemeId).length;

  const handleAdd = () => {
    setThemeBeingEdited(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (theme: ISchoolNoticeTheme) => {
    setThemeBeingEdited(theme);
    setEditorOpen(true);
  };

  const handleDelete = (theme: ISchoolNoticeTheme) => {
    const inUse = noticesUsing(theme);

    confirm({
      title: `Remove ${noticeThemeName(theme)}?`,
      // Names the consequence rather than asking "are you sure": a studio deleting a theme that
      // twelve notices are on wants to know that before it happens, not after.
      message: inUse > 0
        ? `${inUse} ${plurify('notice', inUse)} currently ${inUse === 1 ? 'uses' : 'use'} this theme and will move to ${themeList[0] ? noticeThemeName(themeList[0]) : 'the first theme'}. This cannot be undone.`
        : 'This theme comes off the colour picker. This cannot be undone.',
      positiveText: 'Remove theme',
      onPositive: () => deleteTheme(theme.noticeThemeId)
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        fullScreenOnMobile
        title='Notice themes'
        contentTopPadding={getSpacingCss(2)}
        actions={<Button variant='text' onClick={onClose}>Done</Button>}
      >
        <Stack rowGap={2} className={cn()}>
          <Typography variant='caption'>
            The colours your team can pin a notice on. Everyone at your studio picks from this list.
          </Typography>

          {isLoading ? (
            <CenterProgressSpinner />
          ) : (
            <ManagedItemList<ISchoolNoticeTheme>
              items={themeList}
              itemKey={x => x.noticeThemeId}
              addLabel='New theme'
              onAdd={handleAdd}
              // Mirrors the API's MaxThemesPerSchool. The count is already in hand, so there is no
              // reason to let a studio fill in a whole dialog before being refused.
              disabledReason={themeList.length >= NOTICE_THEME_MAX_PER_SCHOOL
                ? `A school can have at most ${NOTICE_THEME_MAX_PER_SCHOOL} themes`
                : undefined}
              onEdit={handleEdit}
              onDelete={handleDelete}
              // Mirrors the API's rule exactly rather than being stricter: a board always needs one
              // colour to draw notices in, and the last theme is what a school falls back to.
              deleteDisabledReason={() => themeList.length <= 1
                ? 'A board needs at least one theme'
                : undefined}
              renderItem={(theme) => {
                const inUse = noticesUsing(theme);

                return (
                  <HStack breakpoint={false} columnGap={2} alignItems='center'>
                    {/* The row wears the theme. This is the swatch, the label and the legibility
                        check all at once. */}
                    <Box
                      className={cn('sample')}
                      sx={{
                        backgroundColor: theme.backgroundColor,
                        color: theme.textColor,
                        ...noticeThemeBorderSx(theme)
                      }}
                    >
                      <Typography variant='body2' color='inherit' lineHeight={1.3} noWrap>
                        {noticeThemeName(theme)}
                      </Typography>
                    </Box>

                    <Typography variant='caption' noWrap>
                      {inUse > 0 ? `${inUse} ${plurify('notice', inUse)}` : 'Not in use'}
                    </Typography>
                  </HStack>
                );
              }}
            />
          )}
        </Stack>
      </Modal>

      <NoticeThemeEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        theme={themeBeingEdited}
      />
    </>
  );
};
